import { message, Spin, Empty } from 'antd'
import { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import {
    apiGetTagList,
    getEpicsListForTC,
    getSprintListForTC,
} from '../redux/testRun/testRun.service'
import { getCurrentProjectId } from './helpers'

function resolveProjectId(projectId) {
    return projectId || getCurrentProjectId()
}

export function createFetchSprints(projectId) {
    return (params) => fetchSprints({ ...params, projectId: resolveProjectId(projectId) })
}

export function createFetchEpics(projectId) {
    return (params) => fetchEpics({ ...params, projectId: resolveProjectId(projectId) })
}

function useDebouncedCallback(cb, delay = 400) {
    const t = useRef()
    const cbRef = useRef(cb)
    cbRef.current = cb
    const cancel = useCallback(() => {
        if (t.current) window.clearTimeout(t.current)
    }, [])
    const fn = useCallback(
        (...args) => {
            cancel()
            t.current = window.setTimeout(() => cbRef.current(...args), delay)
        },
        [cancel, delay]
    )
    fn.cancel = cancel
    return fn
}

export function useAsyncSelect(fetcher, pageSize = 20) {
    const [open, setOpen] = useState(false)
    const [options, setOptions] = useState([])
    const [loading, setLoading] = useState(false)
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(true)

    const fetcherRef = useRef(fetcher)
    fetcherRef.current = fetcher
    const searchRef = useRef('')
    const loadSeqRef = useRef(0)

    const load = useCallback(
        async (nextPage, nextSearch, replace) => {
            const seq = ++loadSeqRef.current
            setLoading(true)
            try {
                const { items, hasMore: more } = await fetcherRef.current({
                    search: nextSearch,
                    page: nextPage,
                    pageSize,
                })
                if (seq !== loadSeqRef.current) return
                setOptions((prev) => (replace ? items : [...prev, ...items]))
                setHasMore(more)
                setPage(nextPage)
            } catch (e) {
                if (seq !== loadSeqRef.current) return
                message.error('Failed to load options.')
            } finally {
                if (seq === loadSeqRef.current) setLoading(false)
            }
        },
        [pageSize]
    )

    const clearSearchState = useCallback(() => {
        searchRef.current = ''
        setSearch('')
        setHasMore(true)
        setPage(1)
    }, [])

    useEffect(() => {
        searchRef.current = ''
        setOptions([])
        setHasMore(true)
        setPage(1)
        setSearch('')
    }, [fetcher])

    const debouncedSearch = useDebouncedCallback((q) => {
        const cleaned = q.trim()
        searchRef.current = cleaned
        setSearch(cleaned)
        setHasMore(true)
        load(1, cleaned, true)
    }, 400)

    const onDropdownVisibleChange = useCallback(
        (v) => {
            setOpen(v)
            if (v) {
                debouncedSearch.cancel()
                clearSearchState()
                load(1, '', true)
                return
            }
            // Closing (select, blur, or clear) must drop the typed filter
            // so the next open shows the full sprint list.
            debouncedSearch.cancel()
            loadSeqRef.current += 1
            clearSearchState()
        },
        [load, clearSearchState, debouncedSearch]
    )

    const onSelect = useCallback(() => {
        debouncedSearch.cancel()
        clearSearchState()
    }, [debouncedSearch, clearSearchState])

    const onSearch = useCallback(
        (q) => {
            const next = String(q || '')
            searchRef.current = next.trim()
            setSearch(next)
            if (!next.trim()) {
                debouncedSearch.cancel()
                setHasMore(true)
                load(1, '', true)
                return
            }
            debouncedSearch(next)
        },
        [debouncedSearch, load]
    )

    const onClear = useCallback(() => {
        debouncedSearch.cancel()
        loadSeqRef.current += 1
        clearSearchState()
        setOptions([])
        load(1, '', true)
    }, [clearSearchState, debouncedSearch, load])

    const onPopupScroll = useCallback(
        (e) => {
            const el = e.currentTarget
            const nearBottom =
                el.scrollTop + el.clientHeight >= el.scrollHeight - 10
            if (nearBottom && hasMore && !loading) {
                load(page + 1, searchRef.current, false)
            }
        },
        [hasMore, loading, load, page]
    )

    const notFoundContent = useMemo(
        () =>
            loading ? (
                <div style={{ padding: 8, textAlign: 'center' }}>
                    <Spin size="small" />
                </div>
            ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ),
        [loading]
    )

    return {
        open,
        options,
        loading,
        searchValue: search,
        notFoundContent,
        onDropdownVisibleChange,
        onPopupScroll,
        onSearch,
        onSelect,
        onClear,
        reset: () => {
            debouncedSearch.cancel()
            loadSeqRef.current += 1
            clearSearchState()
            setOptions([])
        },
    }
}

export async function fetchEpics({ search, page, pageSize, projectId }) {
    const resolvedProjectId = resolveProjectId(projectId)
    if (!resolvedProjectId) {
        return { items: [], hasMore: false }
    }
    const payload = {
        skip: (page - 1) * pageSize,
        limit: pageSize,
        projectId: resolvedProjectId,
        ...(search ? { title: search } : {}),
        filter: {
            ...(search ? { title: search } : {}),
        },
    }
    const params = new URLSearchParams(payload).toString()
    const res = await getEpicsListForTC(params)
    const data = Array.isArray(res?.data) ? res.data : res?.data?.data ?? []
    return {
        items: data.map((e) => {
            const epicKey =
                e.epicKey != null && e.epicKey !== '' ? String(e.epicKey) : ''
            return {
                value: e._id,
                epicKey,
                label: e.title || 'Epic',
            }
        }),
        hasMore: data.length === pageSize,
    }
}

export async function fetchSprints({ search, page, pageSize, projectId }) {
    const resolvedProjectId = resolveProjectId(projectId)
    if (!resolvedProjectId) {
        return { items: [], hasMore: false }
    }
    const payload = {
        skip: (page - 1) * pageSize,
        limit: pageSize,
        ...(search ? { search } : {}),
        filter: {
            populate: {
                name: 'projectId',
                val: 'name',
            },
            projectId: resolvedProjectId,
        },
    }
    const res = await getSprintListForTC(payload)
    const list = res?.data?.data ?? res?.data ?? []
    const isNext = res?.data?.is_next
    return {
        items: list.map((s) => ({ value: s._id, label: s.title })),
        hasMore:
            typeof isNext === 'boolean' ? isNext : list.length === pageSize,
    }
}

export async function fetchTags({ search, page, pageSize, projectId }) {
    const resolvedProjectId = resolveProjectId(projectId)
    if (!resolvedProjectId) {
        return { items: [], hasMore: false }
    }
    const params = {
        skip: (page - 1) * pageSize,
        limit: pageSize,
        projectId: resolvedProjectId,
        sort: 'name',
        sortType: 'asc',
        ...(search ? { search } : {}),
    }
    const res = await apiGetTagList(params)
    const list = res?.data?.data ?? res?.data ?? []
    const isNext = res?.data?.isNext
    return {
        items: list.map((t) => ({ value: t._id, label: t.name })),
        hasMore:
            typeof isNext === 'boolean' ? isNext : list.length === pageSize,
    }
}
